import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { cn } from '../../../lib/utils';
import {
  Clock, Building2, Users, Calendar, Shield, AlertTriangle,
  Settings, Save, Plus, Trash2, X, Sparkles, ScanLine, CheckCircle2,
  Loader2, RefreshCw, Info, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import TeamsManager from '../../../components/shared/TeamsManager';
import { guardWrite } from '../../../lib/modeGuard';
import { useAppMode } from '../../../contexts/AppModeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { capabilitiesFor } from '../../../lib/permissions';
import * as schedulerApi from '../../../lib/api/schedulerApi';
import OverbookingLimitsSection from './v2/OverbookingLimitsSection';
import VisitTypeSettings from './v2/VisitTypeSettings';
import BookingWidgetSettings from './v2/BookingWidgetSettings';
import {
  DEMO_OVERBOOK_LOCATIONS, DEMO_OB_PROVIDER_CONFIG, DEMO_OB_GLOBAL_RULES,
} from '../../../lib/fixtures/schedulerV2Demo';

const Toggle = ({ on, onToggle, testId }) => (
  <button onClick={onToggle} className={cn("relative inline-flex h-5 w-9 items-center rounded-full transition-colors", on ? "bg-[#111]" : "bg-slate-200")}
    style={{ border: 'none', cursor: 'pointer' }} data-testid={testId}>
    <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform" style={{ transform: on ? 'translateX(18px)' : 'translateX(2px)' }} />
  </button>
);

const InputField = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <p className="text-[10px] text-slate-500 mb-1">{label}</p>
    <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs outline-none focus:border-slate-400" data-testid={testId} />
  </div>
);

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DEMO_PROVIDERS = [
  { id: 'dr-chen', name: 'Dr. Chen', specialty: 'Internal Medicine', active: true, start: '8:00 AM', end: '5:00 PM', days: ['Mon','Tue','Wed','Thu','Fri'], maxDaily: 16, newPatientSlots: 2, lunchStart: '12:00 PM', lunchEnd: '1:00 PM' },
  { id: 'dr-wilson', name: 'Dr. Wilson', specialty: 'Family Medicine', active: true, start: '8:30 AM', end: '4:30 PM', days: ['Mon','Tue','Wed','Thu','Fri','Sat'], maxDaily: 14, newPatientSlots: 3, lunchStart: '12:30 PM', lunchEnd: '1:30 PM' },
  { id: 'dr-roberts', name: 'Dr. Roberts', specialty: 'Pediatrics', active: true, start: '9:00 AM', end: '4:00 PM', days: ['Mon','Tue','Wed','Thu','Fri'], maxDaily: 18, newPatientSlots: 4, lunchStart: '12:00 PM', lunchEnd: '12:30 PM' },
];

const DEMO_LOCATIONS = [
  { id: 'main', name: 'Main Office', address: '100 Medical Pkwy', start: '7:30 AM', end: '6:00 PM', days: ['Mon','Tue','Wed','Thu','Fri','Sat'], rooms: 5 },
  { id: 'north', name: 'North Clinic', address: '450 Cedar Blvd', start: '8:00 AM', end: '5:00 PM', days: ['Mon','Tue','Wed','Thu','Fri'], rooms: 4 },
];

const DEMO_DURATIONS = [
  { id: 'd1', type: 'New Patient', duration: 45, buffer: 5 },
  { id: 'd2', type: 'Follow-up', duration: 20, buffer: 5 },
  { id: 'd3', type: 'Sick Visit', duration: 15, buffer: 5 },
  { id: 'd4', type: 'Wellness / Annual', duration: 45, buffer: 10 },
  { id: 'd5', type: 'Procedure', duration: 60, buffer: 15 },
  { id: 'd6', type: 'Telehealth', duration: 20, buffer: 0 },
];

const DEMO_BLOCKS = [
  { id: 'b1', name: 'Staff Meeting', recurrence: 'Weekly — Mon 8:00-8:30 AM', type: 'admin', active: true },
  { id: 'b2', name: 'Dr. Chen Vacation', recurrence: 'Apr 21-25, 2026', type: 'time_off', active: true },
  { id: 'b3', name: 'System Maintenance', recurrence: 'First Sun monthly 6:00-8:00 AM', type: 'system', active: true },
];

const normEntity = (e) => ({
  id: e.id || e._id,
  name: e.name || e.full_name || '',
  specialty: e.specialty || e.role || '',
  active: e.active ?? true,
  start: e.start || '8:00 AM',
  end: e.end || '5:00 PM',
  days: e.days || ['Mon','Tue','Wed','Thu','Fri'],
  maxDaily: e.max_daily ?? e.maxDaily ?? 16,
  newPatientSlots: e.new_patient_slots ?? e.newPatientSlots ?? 2,
  lunchStart: e.lunch_start || e.lunchStart || '12:00 PM',
  lunchEnd: e.lunch_end || e.lunchEnd || '1:00 PM',
});

const normLocation = (l) => ({
  id: l.id || l._id,
  name: l.name,
  address: l.address || '',
  start: l.hours_start || l.start || '8:00 AM',
  end: l.hours_end || l.end || '5:00 PM',
  days: l.days || ['Mon','Tue','Wed','Thu','Fri'],
  rooms: l.rooms ?? 0,
});

const normVisitType = (v) => ({
  id: v.id || v._id,
  type: v.name || v.type,
  duration: v.duration_min ?? v.duration ?? 30,
  buffer: v.buffer_min ?? v.buffer ?? 0,
});

const normOverride = (o) => ({
  id: o.id || o._id,
  name: o.name || o.reason || 'Block',
  recurrence: o.recurrence || o.recurrence_label || `${o.start || ''} – ${o.end || ''}`.trim(),
  type: o.type || 'admin',
  active: o.active ?? true,
  _raw: o,
});

const SchedulerSettings = () => {
  const [section, setSection] = useState('providers');
  const { isDemo } = useAppMode();
  const { user } = useAuth();
  const { canSchedulerAdmin } = capabilitiesFor(user);
  const allowSchedulerAdmin = isDemo || canSchedulerAdmin;

  // Provider state
  const [providers, setProviders] = useState(isDemo ? DEMO_PROVIDERS : []);
  const [providersLoading, setProvidersLoading] = useState(!isDemo);
  const [providersError, setProvidersError] = useState(null);
  const [editProvider, setEditProvider] = useState(null);
  const [addProviderOpen, setAddProviderOpen] = useState(false);

  // Location state
  const [locations, setLocations] = useState(isDemo ? DEMO_LOCATIONS : []);
  const [locationsLoading, setLocationsLoading] = useState(!isDemo);
  const [locationsError, setLocationsError] = useState(null);
  const [editLocation, setEditLocation] = useState(null);
  const [addLocationOpen, setAddLocationOpen] = useState(false);

  // Duration (visit types) state
  const [durations, setDurations] = useState(isDemo ? DEMO_DURATIONS : []);
  const [durationsLoading, setDurationsLoading] = useState(!isDemo);
  const [durationsError, setDurationsError] = useState(null);
  const [editDuration, setEditDuration] = useState(null);
  const [addDurationOpen, setAddDurationOpen] = useState(false);

  // Blocks (overrides) state
  const [blocks, setBlocks] = useState(isDemo ? DEMO_BLOCKS : []);
  const [blocksLoading, setBlocksLoading] = useState(!isDemo);
  const [blocksError, setBlocksError] = useState(null);
  const [editBlock, setEditBlock] = useState(null);
  const [addBlockOpen, setAddBlockOpen] = useState(false);

  // Policies (AI config)
  const [overbookEnabled, setOverbookEnabled] = useState(true);
  const [overbookThreshold, setOverbookThreshold] = useState(30);
  const [selfCheckIn, setSelfCheckIn] = useState(true);
  const [autoReminders, setAutoReminders] = useState(true);
  const [lensUpload, setLensUpload] = useState(true);
  const [policiesLoading, setPoliciesLoading] = useState(!isDemo);
  const [policiesError, setPoliciesError] = useState(null);
  const [policiesSaving, setPoliciesSaving] = useState(false);

  /* ── LOADERS ── */
  const loadProviders = useCallback(async () => {
    if (isDemo) { setProviders(DEMO_PROVIDERS); setProvidersLoading(false); setProvidersError(null); return; }
    setProvidersLoading(true); setProvidersError(null);
    const res = await schedulerApi.listEntities({ active: true });
    if (res.ok) {
      setProviders((res.data?.entities || res.data?.items || []).map(normEntity));
    } else setProvidersError(res.error);
    setProvidersLoading(false);
  }, [isDemo]);

  const loadLocations = useCallback(async () => {
    if (isDemo) { setLocations(DEMO_LOCATIONS); setLocationsLoading(false); setLocationsError(null); return; }
    setLocationsLoading(true); setLocationsError(null);
    const res = await schedulerApi.listLocations();
    if (res.ok) {
      setLocations((res.data?.locations || res.data?.items || []).map(normLocation));
    } else setLocationsError(res.error);
    setLocationsLoading(false);
  }, [isDemo]);

  const loadDurations = useCallback(async () => {
    if (isDemo) { setDurations(DEMO_DURATIONS); setDurationsLoading(false); setDurationsError(null); return; }
    setDurationsLoading(true); setDurationsError(null);
    const res = await schedulerApi.listVisitTypes();
    if (res.ok) {
      setDurations((res.data?.visit_types || res.data?.items || []).map(normVisitType));
    } else setDurationsError(res.error);
    setDurationsLoading(false);
  }, [isDemo]);

  const loadBlocks = useCallback(async () => {
    if (isDemo) { setBlocks(DEMO_BLOCKS); setBlocksLoading(false); setBlocksError(null); return; }
    // Vendor mode: Calendar Blocks are managed per-config via vendor §16
    // (not via a global /overrides endpoint). Skip the call instead of
    // hitting the login host and surfacing a 403/404.
    if (!schedulerApi.isInternalSchedulerSupported()) {
      setBlocks([]);
      setBlocksLoading(false);
      setBlocksError({ code: 'NOT_SUPPORTED_IN_VENDOR_MODE', message: 'Calendar Blocks are managed per-config in vendor mode.' });
      return;
    }
    setBlocksLoading(true); setBlocksError(null);
    const res = await schedulerApi.listOverrides();
    if (res.ok) {
      setBlocks((res.data?.overrides || res.data?.items || []).map(normOverride));
    } else setBlocksError(res.error);
    setBlocksLoading(false);
  }, [isDemo]);

  const loadPolicies = useCallback(async () => {
    if (isDemo) { setPoliciesLoading(false); setPoliciesError(null); return; }
    // Vendor mode: Booking Policies live inside the vendor AI Assist flow
    // (config/ai-assist + config/ai-apply, §20). The legacy /policies doc
    // doesn't exist on the vendor edge. Skip to prevent 404 on login host.
    if (!schedulerApi.isInternalSchedulerSupported()) {
      setPoliciesLoading(false);
      setPoliciesError({ code: 'NOT_SUPPORTED_IN_VENDOR_MODE', message: 'Booking Policies are edited via vendor AI Assist in vendor mode.' });
      return;
    }
    setPoliciesLoading(true); setPoliciesError(null);
    const res = await schedulerApi.getAiConfig();
    if (res.ok) {
      const cfg = res.data || {};
      setOverbookEnabled(cfg.overbooking_enabled ?? true);
      setOverbookThreshold(cfg.overbooking_threshold ?? 30);
      setSelfCheckIn(cfg.self_checkin ?? true);
      setAutoReminders(cfg.auto_reminders ?? true);
      setLensUpload(cfg.lens_upload ?? true);
    } else setPoliciesError(res.error);
    setPoliciesLoading(false);
  }, [isDemo]);

  useEffect(() => {
    loadProviders(); loadLocations(); loadDurations(); loadBlocks(); loadPolicies();
  }, [loadProviders, loadLocations, loadDurations, loadBlocks, loadPolicies]);

  // Generic form state for add dialogs
  const [form, setForm] = useState({});
  const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const toggleFormDay = (day) => setForm(prev => ({ ...prev, days: (prev.days || []).includes(day) ? prev.days.filter(d => d !== day) : [...(prev.days || []), day] }));

  const SECTIONS = [
    { id: 'providers', label: 'Provider Hours', icon: Users },
    { id: 'locations', label: 'Location Hours', icon: Building2 },
    { id: 'durations', label: 'Appointment Types', icon: Clock },
    { id: 'visitTypes', label: 'Visit Types', icon: Sparkles },
    { id: 'bookingWidget', label: 'Booking Widget', icon: ExternalLink },
    { id: 'blocks', label: 'Calendar Blocks', icon: Calendar },
    { id: 'overbooking', label: 'Overbooking Limits', icon: AlertTriangle },
    { id: 'policies', label: 'Booking Policies', icon: Shield },
    { id: 'overbooking', label: 'Overbooking Limits', icon: Sparkles },
    { id: 'integrations', label: 'Import & Lens', icon: ScanLine },
    { id: 'teams', label: 'Team & Roles', icon: Users },
  ];

  /* ── Overbooking state — demo fixtures or empty in real mode ── */
  const [overbookLocations, setOverbookLocations] = useState(() => {
    if (!isDemo) return [];
    // Merge per-provider engine config into location providers.
    return DEMO_OVERBOOK_LOCATIONS.map((loc) => ({
      ...loc,
      providers: loc.providers.map((p) => ({
        ...p,
        config: DEMO_OB_PROVIDER_CONFIG[p.id] || {
          overbookWindow: 'all_day',
          autoContactWaitlist: false,
          collisionProtocol: 'offer_next_slot',
          goodwillAmount: 0,
        },
      })),
    }));
  });
  const [approvalMode, setApprovalMode] = useState('manager');
  const [globalRules, setGlobalRules] = useState(isDemo ? DEMO_OB_GLOBAL_RULES : null);

  const LoadState = ({ label, testId }) => (
    <div className="flex items-center justify-center py-8" data-testid={testId || 'section-loading'}>
      <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
      <span className="ml-2 text-xs text-slate-500">{label || 'Loading…'}</span>
    </div>
  );
  const ErrorState = ({ error, onRetry, testId }) => {
    // When the "error" is just the vendor-mode soft-skip we surfaced from
    // the API client, render it as an informational banner instead of the
    // red failure state. This keeps the Settings tab usable in vendor
    // mode without scaring users with a red alert.
    if (error?.code === 'NOT_SUPPORTED_IN_VENDOR_MODE') {
      return (
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 flex items-start gap-2" data-testid={(testId || 'section-error') + '-vendor-notice'}>
          <Info className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-blue-800 font-medium">Not available in vendor mode</p>
            <p className="text-[10px] text-blue-700 mt-0.5">{error.message}</p>
          </div>
        </div>
      );
    }
    return (
      <div className="p-3 rounded-lg bg-red-50 border border-red-100 flex items-center gap-2" data-testid={testId || 'section-error'}>
        <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
        <span className="text-xs text-red-700 flex-1">{error?.message || 'Request failed'}</span>
        <Button size="sm" variant="outline" className="text-[10px] gap-1" onClick={onRetry}>
          <RefreshCw className="h-3 w-3" /> Retry
        </Button>
      </div>
    );
  };
  const EmptyState = ({ label, testId }) => (
    <div className="py-8 text-center" data-testid={testId || 'section-empty'}>
      <Info className="h-5 w-5 text-slate-300 mx-auto mb-1" />
      <p className="text-[11px] text-slate-500">{label || 'No entries yet.'}</p>
    </div>
  );

  /* ═══ EDIT DIALOGS ═══ */
  const ProviderForm = ({ data, onSave, onClose, title }) => (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="provider-form-dialog">
        <DialogHeader><DialogTitle className="text-base font-semibold">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <InputField label="Name" value={data.name || ''} onChange={v => onSave({ ...data, name: v })} testId="prov-name" />
            <InputField label="Specialty" value={data.specialty || ''} onChange={v => onSave({ ...data, specialty: v })} testId="prov-specialty" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <InputField label="Start Time" value={data.start || ''} onChange={v => onSave({ ...data, start: v })} testId="prov-start" />
            <InputField label="End Time" value={data.end || ''} onChange={v => onSave({ ...data, end: v })} testId="prov-end" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 mb-1">Working Days</p>
            <div className="flex gap-1">{DAYS.map(d => (
              <button key={d} onClick={() => { const days = (data.days || []).includes(d) ? data.days.filter(x => x !== d) : [...(data.days || []), d]; onSave({ ...data, days }); }}
                className={cn("px-2 py-1 rounded text-[10px] font-medium", (data.days || []).includes(d) ? "bg-[#111] text-white" : "bg-slate-100 text-slate-500")}
                style={{ border: 'none', cursor: 'pointer' }}>{d}</button>
            ))}</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <InputField label="Max Daily" value={String(data.maxDaily || '')} onChange={v => onSave({ ...data, maxDaily: parseInt(v) || 0 })} testId="prov-max" />
            <InputField label="New Patient Slots" value={String(data.newPatientSlots || '')} onChange={v => onSave({ ...data, newPatientSlots: parseInt(v) || 0 })} testId="prov-new" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <InputField label="Lunch Start" value={data.lunchStart || ''} onChange={v => onSave({ ...data, lunchStart: v })} testId="prov-lunch-start" />
            <InputField label="Lunch End" value={data.lunchEnd || ''} onChange={v => onSave({ ...data, lunchEnd: v })} testId="prov-lunch-end" />
          </div>
        </div>
        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" size="sm" className="text-xs" onClick={() => onClose(false)}>Cancel</Button>
          <Button size="sm" className="text-xs bg-[#111] hover:bg-[#222]" onClick={() => onClose(true)}  disabled={!allowSchedulerAdmin} data-testid="save-provider-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="flex gap-4" data-testid="scheduler-settings">
      <div className="w-48 space-y-0.5 shrink-0">
        {!allowSchedulerAdmin && (
          <div className="p-2 mb-2 rounded-lg bg-amber-50 border border-amber-100 flex items-start gap-1.5" data-testid="scheduler-readonly-banner">
            <Shield className="h-3 w-3 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-[9px] text-amber-700 leading-tight">Read-only view. Admin access required for changes.</p>
          </div>
        )}
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            className={cn("w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors text-left",
              section === s.id ? "bg-[#111] text-white" : "text-slate-600 hover:bg-slate-100"
            )} style={{ border: 'none', cursor: 'pointer' }} data-testid={`settings-nav-${s.id}`}>
            <s.icon className="h-3.5 w-3.5" /> {s.label}
          </button>
        ))}
      </div>

      <div className="flex-1 space-y-4">
        {/* ═══ PROVIDERS ═══ */}
        {section === 'providers' && (
          <div className="space-y-3" data-testid="settings-providers">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-800">Provider Hours & Availability</p>
              <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => { setForm({ name: '', specialty: '', start: '8:00 AM', end: '5:00 PM', days: ['Mon','Tue','Wed','Thu','Fri'], maxDaily: 16, newPatientSlots: 2, lunchStart: '12:00 PM', lunchEnd: '1:00 PM', active: true }); setAddProviderOpen(true); }}  disabled={!allowSchedulerAdmin} data-testid="add-provider-btn"><Plus className="h-3 w-3" />Add Provider</Button>
            </div>
            {!isDemo && (
              <div className="p-2.5 rounded-lg bg-blue-50 border border-blue-100 flex items-start gap-2" data-testid="providers-api-info">
                <Info className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-[10px] text-blue-700">Provider records come from <b>Team & Roles</b>. Hours/config changes here will save when the Scheduler Config API is wired in the next slice.</p>
              </div>
            )}
            {providersLoading && <LoadState label="Loading providers…" testId="providers-loading" />}
            {!providersLoading && providersError && <ErrorState error={providersError} onRetry={loadProviders} testId="providers-error" />}
            {!providersLoading && !providersError && providers.length === 0 && <EmptyState label="No providers configured. Add one from Team & Roles." testId="providers-empty" />}
            {!providersLoading && !providersError && providers.map(p => (
              <Card key={p.id} className="border-slate-200" data-testid={`provider-config-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div><p className="text-sm font-semibold text-slate-800">{p.name}</p><p className="text-[10px] text-slate-400">{p.specialty}</p></div>
                    <div className="flex items-center gap-3">
                      <Toggle on={p.active} onToggle={() => guardWrite('Toggle provider', () => setProviders(prev => prev.map(x => x.id === p.id ? { ...x, active: !x.active } : x)), isDemo)} testId={`toggle-${p.id}`} />
                      <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => setEditProvider({ ...p })} data-testid={`edit-provider-${p.id}`}>Edit</Button>
                      <Button variant="ghost" size="sm" className="h-7 text-[10px] text-red-500" onClick={() => guardWrite('Remove provider', () => { setProviders(prev => prev.filter(x => x.id !== p.id)); toast.success('Provider removed'); }, isDemo)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-3 text-[10px]">
                    <div className="p-2 rounded-lg bg-slate-50"><p className="text-slate-400 uppercase text-[9px]">Hours</p><p className="font-medium text-slate-700">{p.start} – {p.end}</p></div>
                    <div className="p-2 rounded-lg bg-slate-50"><p className="text-slate-400 uppercase text-[9px]">Days</p><p className="font-medium text-slate-700">{p.days.join(', ')}</p></div>
                    <div className="p-2 rounded-lg bg-slate-50"><p className="text-slate-400 uppercase text-[9px]">Daily Max</p><p className="font-medium text-slate-700">{p.maxDaily} appts</p></div>
                    <div className="p-2 rounded-lg bg-slate-50"><p className="text-slate-400 uppercase text-[9px]">New Patients/Day</p><p className="font-medium text-slate-700">{p.newPatientSlots} slots</p></div>
                    <div className="p-2 rounded-lg bg-slate-50"><p className="text-slate-400 uppercase text-[9px]">Lunch Block</p><p className="font-medium text-slate-700">{p.lunchStart} – {p.lunchEnd}</p></div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {editProvider && <ProviderForm data={editProvider} onSave={setEditProvider} onClose={(save) => { if (save) guardWrite('Update provider', () => { setProviders(prev => prev.map(p => p.id === editProvider.id ? editProvider : p)); toast.success('Provider updated'); }, isDemo); setEditProvider(null); }} title={`Edit ${editProvider.name}`} />}
            {addProviderOpen && <ProviderForm data={form} onSave={setForm} onClose={(save) => { if (save && form.name) guardWrite('Add provider', () => { setProviders(prev => [...prev, { ...form, id: `prov-${Date.now()}` }]); toast.success(`${form.name} added`); }, isDemo); setAddProviderOpen(false); }} title="Add Provider" />}
          </div>
        )}

        {/* ═══ LOCATIONS ═══ */}
        {section === 'locations' && (
          <div className="space-y-3" data-testid="settings-locations">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-800">Location Hours</p>
              <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => { setForm({ name: '', address: '', start: '8:00 AM', end: '5:00 PM', days: ['Mon','Tue','Wed','Thu','Fri'], rooms: 4 }); setAddLocationOpen(true); }}  disabled={!allowSchedulerAdmin} data-testid="add-location-btn"><Plus className="h-3 w-3" />Add Location</Button>
            </div>
            {locationsLoading && <LoadState label="Loading locations…" testId="locations-loading" />}
            {!locationsLoading && locationsError && <ErrorState error={locationsError} onRetry={loadLocations} testId="locations-error" />}
            {!locationsLoading && !locationsError && locations.length === 0 && <EmptyState label="No locations configured yet." testId="locations-empty" />}
            {!locationsLoading && !locationsError && locations.map(l => (
              <Card key={l.id} className="border-slate-200" data-testid={`location-config-${l.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div><p className="text-sm font-semibold text-slate-800">{l.name}</p><p className="text-[10px] text-slate-400">{l.address}</p></div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => setEditLocation({ ...l })} data-testid={`edit-location-${l.id}`}>Edit</Button>
                      <Button variant="ghost" size="sm" className="h-7 text-[10px] text-red-500" onClick={() => guardWrite('Remove location', async () => {
                        if (isDemo) { setLocations(prev => prev.filter(x => x.id !== l.id)); toast.success('Location removed'); return; }
                        const res = await schedulerApi.deleteLocation(l.id);
                        if (res.ok) { toast.success('Location removed'); loadLocations(); } else toast.error('Failed to remove', { description: res.error?.message });
                      }, isDemo)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3 text-[10px]">
                    <div className="p-2 rounded-lg bg-slate-50"><p className="text-slate-400 uppercase text-[9px]">Office Hours</p><p className="font-medium text-slate-700">{l.start} – {l.end}</p></div>
                    <div className="p-2 rounded-lg bg-slate-50"><p className="text-slate-400 uppercase text-[9px]">Open Days</p><p className="font-medium text-slate-700">{(l.days || []).join(', ')}</p></div>
                    <div className="p-2 rounded-lg bg-slate-50"><p className="text-slate-400 uppercase text-[9px]">Exam Rooms</p><p className="font-medium text-slate-700">{l.rooms}</p></div>
                    <div className="p-2 rounded-lg bg-slate-50"><p className="text-slate-400 uppercase text-[9px]">Status</p><p className="font-medium text-emerald-600">Open</p></div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {editLocation && (
              <Dialog open onOpenChange={() => setEditLocation(null)}><DialogContent className="sm:max-w-sm"><DialogHeader><DialogTitle className="text-base font-semibold">Edit {editLocation.name}</DialogTitle></DialogHeader>
                <div className="space-y-3"><InputField label="Name" value={editLocation.name} onChange={v => setEditLocation({...editLocation, name: v})} /><InputField label="Address" value={editLocation.address} onChange={v => setEditLocation({...editLocation, address: v})} /><div className="grid grid-cols-2 gap-2"><InputField label="Open" value={editLocation.start} onChange={v => setEditLocation({...editLocation, start: v})} /><InputField label="Close" value={editLocation.end} onChange={v => setEditLocation({...editLocation, end: v})} /></div><InputField label="Rooms" value={String(editLocation.rooms)} onChange={v => setEditLocation({...editLocation, rooms: parseInt(v)||0})} /></div>
                <DialogFooter className="gap-2"><Button variant="outline" size="sm" className="text-xs" onClick={() => setEditLocation(null)}>Cancel</Button><Button size="sm" className="text-xs bg-[#111] hover:bg-[#222]" onClick={() => guardWrite('Update location', async () => {
                  if (isDemo) { setLocations(prev => prev.map(l => l.id === editLocation.id ? editLocation : l)); toast.success('Location updated'); setEditLocation(null); return; }
                  const res = await schedulerApi.updateLocation(editLocation.id, { name: editLocation.name, address: editLocation.address, hours_start: editLocation.start, hours_end: editLocation.end, days: editLocation.days, rooms: editLocation.rooms });
                  if (res.ok) { toast.success('Location updated'); setEditLocation(null); loadLocations(); } else toast.error('Failed to save', { description: res.error?.message });
                }, isDemo)}>Save</Button></DialogFooter>
              </DialogContent></Dialog>
            )}
            {addLocationOpen && (
              <Dialog open onOpenChange={() => setAddLocationOpen(false)}><DialogContent className="sm:max-w-sm"><DialogHeader><DialogTitle className="text-base font-semibold">Add Location</DialogTitle></DialogHeader>
                <div className="space-y-3"><InputField label="Name" value={form.name||''} onChange={v => updateForm('name',v)} /><InputField label="Address" value={form.address||''} onChange={v => updateForm('address',v)} /><div className="grid grid-cols-2 gap-2"><InputField label="Open" value={form.start||''} onChange={v => updateForm('start',v)} /><InputField label="Close" value={form.end||''} onChange={v => updateForm('end',v)} /></div><InputField label="Rooms" value={String(form.rooms||'')} onChange={v => updateForm('rooms',parseInt(v)||0)} /></div>
                <DialogFooter className="gap-2"><Button variant="outline" size="sm" className="text-xs" onClick={() => setAddLocationOpen(false)}>Cancel</Button><Button size="sm" className="text-xs bg-[#111] hover:bg-[#222]" onClick={() => { if(form.name) guardWrite('Add location', async () => {
                  if (isDemo) { setLocations(prev => [...prev, {...form, id: `loc-${Date.now()}`, days: form.days||['Mon','Tue','Wed','Thu','Fri']}]); toast.success(`${form.name} added`); return; }
                  const res = await schedulerApi.createLocation({ name: form.name, address: form.address, hours_start: form.start, hours_end: form.end, days: form.days || ['Mon','Tue','Wed','Thu','Fri'], rooms: form.rooms });
                  if (res.ok) { toast.success(`${form.name} added`); loadLocations(); } else toast.error('Failed to add', { description: res.error?.message });
                }, isDemo); setAddLocationOpen(false); }}  disabled={!allowSchedulerAdmin} data-testid="save-location-btn">Save</Button></DialogFooter>
              </DialogContent></Dialog>
            )}
          </div>
        )}

        {/* ═══ DURATIONS ═══ */}
        {section === 'durations' && (
          <div className="space-y-3" data-testid="settings-durations">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-800">Appointment Types & Durations</p>
              <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => { setForm({ type: '', duration: 30, buffer: 5 }); setAddDurationOpen(true); }}  disabled={!allowSchedulerAdmin} data-testid="add-duration-btn"><Plus className="h-3 w-3" />Add Type</Button>
            </div>
            {durationsLoading && <LoadState label="Loading visit types…" testId="durations-loading" />}
            {!durationsLoading && durationsError && <ErrorState error={durationsError} onRetry={loadDurations} testId="durations-error" />}
            {!durationsLoading && !durationsError && durations.length === 0 && <EmptyState label="No visit types configured." testId="durations-empty" />}
            {!durationsLoading && !durationsError && durations.length > 0 && (
            <Card className="border-slate-200"><CardContent className="p-0">
              <div className="grid grid-cols-4 gap-0 text-[10px] font-semibold text-slate-400 uppercase px-4 py-2 border-b border-slate-100"><span>Type</span><span>Duration</span><span>Buffer</span><span>Actions</span></div>
              {durations.map(a => (
                <div key={a.id} className="grid grid-cols-4 gap-0 px-4 py-2.5 border-b border-slate-50 last:border-0 items-center" data-testid={`duration-row-${a.id}`}>
                  <span className="text-xs font-medium text-slate-800">{a.type}</span>
                  <span className="text-xs text-slate-600">{a.duration} min</span>
                  <span className="text-xs text-slate-600">{a.buffer} min</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => setEditDuration({...a})}>Edit</Button>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-500" onClick={() => guardWrite('Remove appointment type', async () => {
                      if (isDemo) { setDurations(prev => prev.filter(x => x.id !== a.id)); toast.success('Removed'); return; }
                      const res = await schedulerApi.deleteVisitType(a.id);
                      if (res.ok) { toast.success('Removed'); loadDurations(); } else toast.error('Failed', { description: res.error?.message });
                    }, isDemo)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </CardContent></Card>
            )}
            {(editDuration || addDurationOpen) && (
              <Dialog open onOpenChange={() => { setEditDuration(null); setAddDurationOpen(false); }}><DialogContent className="sm:max-w-xs"><DialogHeader><DialogTitle className="text-base font-semibold">{editDuration ? `Edit ${editDuration.type}` : 'Add Appointment Type'}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <InputField label="Type Name" value={(editDuration || form).type || ''} onChange={v => editDuration ? setEditDuration({...editDuration, type: v}) : updateForm('type', v)} testId="dur-type" />
                  <div className="grid grid-cols-2 gap-2">
                    <InputField label="Duration (min)" value={String((editDuration || form).duration || '')} onChange={v => editDuration ? setEditDuration({...editDuration, duration: parseInt(v)||0}) : updateForm('duration', parseInt(v)||0)} testId="dur-mins" />
                    <InputField label="Buffer (min)" value={String((editDuration || form).buffer || '')} onChange={v => editDuration ? setEditDuration({...editDuration, buffer: parseInt(v)||0}) : updateForm('buffer', parseInt(v)||0)} testId="dur-buffer" />
                  </div>
                </div>
                <DialogFooter className="gap-2"><Button variant="outline" size="sm" className="text-xs" onClick={() => { setEditDuration(null); setAddDurationOpen(false); }}>Cancel</Button>
                  <Button size="sm" className="text-xs bg-[#111] hover:bg-[#222]" onClick={() => {
                    guardWrite(editDuration ? 'Update appointment type' : 'Add appointment type', async () => {
                      if (isDemo) {
                        if (editDuration) { setDurations(prev => prev.map(d => d.id === editDuration.id ? editDuration : d)); toast.success('Updated'); }
                        else if (form.type) { setDurations(prev => [...prev, { ...form, id: `d${Date.now()}` }]); toast.success('Added'); }
                        setEditDuration(null); setAddDurationOpen(false);
                        return;
                      }
                      if (editDuration) {
                        const res = await schedulerApi.updateVisitType(editDuration.id, { name: editDuration.type, duration_min: editDuration.duration, buffer_min: editDuration.buffer });
                        if (res.ok) { toast.success('Updated'); setEditDuration(null); loadDurations(); } else toast.error('Failed', { description: res.error?.message });
                      } else if (form.type) {
                        const res = await schedulerApi.createVisitType({ name: form.type, duration_min: form.duration, buffer_min: form.buffer });
                        if (res.ok) { toast.success('Added'); setAddDurationOpen(false); loadDurations(); } else toast.error('Failed', { description: res.error?.message });
                      }
                    }, isDemo);
                  }}>Save</Button></DialogFooter>
              </DialogContent></Dialog>
            )}
          </div>
        )}

        {/* ═══ BLOCKS ═══ */}
        {section === 'blocks' && (
          <div className="space-y-3" data-testid="settings-blocks">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-800">Calendar Blocks & Time Off</p>
              <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => { setForm({ name: '', recurrence: '', type: 'admin', active: true }); setAddBlockOpen(true); }}  disabled={!allowSchedulerAdmin || !schedulerApi.isInternalSchedulerSupported()} data-testid="add-block-btn"><Plus className="h-3 w-3" />Add Block</Button>
            </div>
            {blocksLoading && <LoadState label="Loading blocks…" testId="blocks-loading" />}
            {!blocksLoading && blocksError && <ErrorState error={blocksError} onRetry={loadBlocks} testId="blocks-error" />}
            {!blocksLoading && !blocksError && blocks.length === 0 && <EmptyState label="No calendar blocks configured." testId="blocks-empty" />}
            {!blocksLoading && !blocksError && blocks.map(b => (
              <div key={b.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid={`block-${b.id}`}>
                <Toggle on={b.active} onToggle={() => guardWrite('Toggle calendar block', async () => {
                  if (isDemo) { setBlocks(prev => prev.map(x => x.id === b.id ? {...x, active: !x.active} : x)); return; }
                  const res = await schedulerApi.updateOverride(b.id, { active: !b.active });
                  if (res.ok) loadBlocks(); else toast.error('Failed', { description: res.error?.message });
                }, isDemo)} testId={`block-toggle-${b.id}`} />
                <div className="flex-1"><p className="text-xs font-medium text-slate-800">{b.name}</p><p className="text-[10px] text-slate-400">{b.recurrence}</p></div>
                <Badge className={cn("text-[8px] border-0", b.type === 'admin' ? 'bg-blue-50 text-blue-600' : b.type === 'time_off' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500')}>{b.type === 'admin' ? 'Admin' : b.type === 'time_off' ? 'Time Off' : 'System'}</Badge>
                <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => setEditBlock({...b})}>Edit</Button>
                <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-500" onClick={() => guardWrite('Remove calendar block', async () => {
                  if (isDemo) { setBlocks(prev => prev.filter(x => x.id !== b.id)); toast.success('Removed'); return; }
                  const res = await schedulerApi.deleteOverride(b.id);
                  if (res.ok) { toast.success('Removed'); loadBlocks(); } else toast.error('Failed', { description: res.error?.message });
                }, isDemo)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            ))}
            {(editBlock || addBlockOpen) && (
              <Dialog open onOpenChange={() => { setEditBlock(null); setAddBlockOpen(false); }}><DialogContent className="sm:max-w-sm"><DialogHeader><DialogTitle className="text-base font-semibold">{editBlock ? 'Edit Block' : 'Add Block'}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <InputField label="Name" value={(editBlock || form).name || ''} onChange={v => editBlock ? setEditBlock({...editBlock, name: v}) : updateForm('name', v)} testId="block-name" />
                  <InputField label="Schedule" value={(editBlock || form).recurrence || ''} onChange={v => editBlock ? setEditBlock({...editBlock, recurrence: v}) : updateForm('recurrence', v)} testId="block-recurrence" />
                  <div><p className="text-[10px] text-slate-500 mb-1">Type</p><div className="flex gap-1">
                    {['admin', 'time_off', 'system'].map(t => (
                      <button key={t} onClick={() => editBlock ? setEditBlock({...editBlock, type: t}) : updateForm('type', t)}
                        className={cn("px-2.5 py-1 rounded text-[10px] font-medium", (editBlock || form).type === t ? "bg-[#111] text-white" : "bg-slate-100 text-slate-600")}
                        style={{ border: 'none', cursor: 'pointer' }}>{t === 'admin' ? 'Admin' : t === 'time_off' ? 'Time Off' : 'System'}</button>
                    ))}</div></div>
                </div>
                <DialogFooter className="gap-2"><Button variant="outline" size="sm" className="text-xs" onClick={() => { setEditBlock(null); setAddBlockOpen(false); }}>Cancel</Button>
                  <Button size="sm" className="text-xs bg-[#111] hover:bg-[#222]" onClick={() => {
                    guardWrite(editBlock ? 'Update calendar block' : 'Add calendar block', async () => {
                      if (isDemo) {
                        if (editBlock) { setBlocks(prev => prev.map(b => b.id === editBlock.id ? editBlock : b)); toast.success('Updated'); }
                        else if (form.name) { setBlocks(prev => [...prev, { ...form, id: `b${Date.now()}`, active: true }]); toast.success('Added'); }
                        setEditBlock(null); setAddBlockOpen(false);
                        return;
                      }
                      if (editBlock) {
                        const res = await schedulerApi.updateOverride(editBlock.id, { name: editBlock.name, recurrence: editBlock.recurrence, type: editBlock.type });
                        if (res.ok) { toast.success('Updated'); setEditBlock(null); loadBlocks(); } else toast.error('Failed', { description: res.error?.message });
                      } else if (form.name) {
                        // Minimal override payload — real backend will normalize
                        const res = await schedulerApi.createOverride({ entity_id: form.entity_id || 'all', start: form.start || new Date().toISOString(), end: form.end || new Date().toISOString(), name: form.name, recurrence: form.recurrence, type: form.type });
                        if (res.ok) { toast.success('Added'); setAddBlockOpen(false); loadBlocks(); } else toast.error('Failed', { description: res.error?.message });
                      }
                    }, isDemo);
                  }}>Save</Button></DialogFooter>
              </DialogContent></Dialog>
            )}
          </div>
        )}

        {/* ═══ OVERBOOKING LIMITS (Edit 6) ═══ */}
        {section === 'overbooking' && (
          <div className="space-y-3" data-testid="settings-overbooking">
            {!isDemo && overbookLocations.length === 0 && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 flex items-start gap-2" data-testid="overbooking-real-empty">
                <Info className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-[10px] text-blue-700">Overbooking limits will populate once the scheduler-config API exposes per-location caps. Save actions are disabled until then.</p>
              </div>
            )}
            <OverbookingLimitsSection
              locations={overbookLocations}
              approvalMode={approvalMode}
              globalRules={globalRules}
              readOnly={!allowSchedulerAdmin}
              onChangeLimit={(locId, provId, next) => {
                setOverbookLocations(prev => prev.map(l => l.id !== locId ? l : {
                  ...l,
                  providers: l.providers.map(p => p.id === provId ? { ...p, limit: next } : p),
                }));
              }}
              onApplyAi={(locId, provId) => {
                setOverbookLocations(prev => prev.map(l => l.id !== locId ? l : {
                  ...l,
                  providers: l.providers.map(p => p.id === provId ? { ...p, limit: p.aiSuggested } : p),
                }));
                toast.success('AI recommendation applied');
              }}
              onToggleAuto={(locId, provId) => {
                setOverbookLocations(prev => prev.map(l => l.id !== locId ? l : {
                  ...l,
                  providers: l.providers.map(p => p.id === provId ? { ...p, autoAdjust: !p.autoAdjust } : p),
                }));
              }}
              onChangeLocationCap={(locId, next) => {
                setOverbookLocations(prev => prev.map(l => l.id === locId ? { ...l, locationCap: next } : l));
              }}
              onChangeApprovalMode={(mode) => {
                setApprovalMode(mode);
              }}
              onChangeProviderConfig={(provId, key, value) => {
                setOverbookLocations(prev => prev.map(l => ({
                  ...l,
                  providers: l.providers.map(p => p.id === provId
                    ? { ...p, config: { ...(p.config || {}), [key]: value } }
                    : p),
                })));
              }}
              onChangeGlobalRules={(next) => {
                setGlobalRules(next);
              }}
            />
          </div>
        )}

        {/* ═══ POLICIES ═══ */}
        {section === 'policies' && (
          <div className="space-y-4" data-testid="settings-policies">
            <p className="text-sm font-semibold text-slate-800">Booking Policies</p>
            {policiesLoading && <LoadState label="Loading AI configuration…" testId="policies-loading" />}
            {!policiesLoading && policiesError && <ErrorState error={policiesError} onRetry={loadPolicies} testId="policies-error" />}
            {!policiesLoading && !policiesError && (<>
            <Card className="border-slate-200"><CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /><div><p className="text-xs font-semibold text-slate-800">Smart Overbooking</p><p className="text-[10px] text-slate-400">Double-book based on patient no-show history</p></div></div>
                <Toggle on={overbookEnabled} onToggle={() => setOverbookEnabled(!overbookEnabled)} testId="toggle-overbook" />
              </div>
              {overbookEnabled && <div className="pl-6 space-y-2"><div className="flex items-center gap-3"><span className="text-[10px] text-slate-500 w-32">No-show threshold</span><input type="range" min="10" max="60" value={overbookThreshold} onChange={e => setOverbookThreshold(e.target.value)} className="flex-1 h-1" data-testid="overbook-threshold" /><span className="text-xs font-semibold text-slate-800 w-10">{overbookThreshold}%</span></div></div>}
            </CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-4 space-y-3">
              {[{ label: 'Patient self-check-in', desc: 'Allow check-in via Patient Hub', on: selfCheckIn, toggle: () => setSelfCheckIn(!selfCheckIn), id: 'self-checkin' },
                { label: 'Automated reminders', desc: '48h, 24h, 2h reminders', on: autoReminders, toggle: () => setAutoReminders(!autoReminders), id: 'auto-reminders' },
                { label: 'Lens document upload', desc: 'Import schedules and patient data via Lens', on: lensUpload, toggle: () => setLensUpload(!lensUpload), id: 'lens-upload' },
              ].map(p => (<div key={p.id} className="flex items-center justify-between py-1.5"><div><p className="text-xs font-medium text-slate-800">{p.label}</p><p className="text-[10px] text-slate-400">{p.desc}</p></div><Toggle on={p.on} onToggle={p.toggle} testId={`toggle-${p.id}`} /></div>))}
            </CardContent></Card>
            <Button className="bg-[#111] hover:bg-[#222] text-xs gap-1.5" disabled={!allowSchedulerAdmin || policiesSaving} onClick={() => guardWrite('Save booking policies', async () => {
              if (isDemo) { toast.success('Settings saved'); return; }
              setPoliciesSaving(true);
              const res = await schedulerApi.updateAiConfig({
                overbooking_enabled: overbookEnabled,
                overbooking_threshold: Number(overbookThreshold),
                self_checkin: selfCheckIn,
                auto_reminders: autoReminders,
                lens_upload: lensUpload,
              });
              setPoliciesSaving(false);
              if (res.ok) toast.success('Settings saved'); else toast.error('Failed to save', { description: res.error?.message });
            }, isDemo)} data-testid="save-settings">
              {policiesSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} {policiesSaving ? 'Saving…' : 'Save Changes'}
            </Button>
            </>)}
          </div>
        )}

        {/* ═══ IMPORT & LENS ═══ */}
        {section === 'overbooking' && <OverbookingSettings allowWrite={allowSchedulerAdmin} />}

        {section === 'integrations' && (
          <div className="space-y-3" data-testid="settings-integrations">
            <p className="text-sm font-semibold text-slate-800">Import & Lens Upload</p>
            <p className="text-xs text-slate-500">Upload existing data from third-party EHR systems.</p>
            <div className="grid grid-cols-2 gap-3">
              {[{ title: 'Appointment Summary', desc: 'Upload daily/weekly exports', icon: Calendar },
                { title: 'Patient Roster', desc: 'Import demographics and insurance', icon: Users },
                { title: 'Provider Schedule', desc: 'Import availability templates', icon: Clock },
                { title: 'Insurance Verification', desc: 'Scan insurance cards', icon: Shield },
              ].map((item, i) => (
                <Card key={i} className="border-slate-200 hover:border-violet-200 transition-colors cursor-pointer" onClick={() => toast.info(`Opening Lens for ${item.title}...`)} data-testid={`lens-import-${i}`}>
                  <CardContent className="p-4 text-center space-y-2">
                    <div className="h-10 w-10 rounded-lg bg-violet-50 flex items-center justify-center mx-auto"><item.icon className="h-5 w-5 text-violet-500" /></div>
                    <p className="text-xs font-semibold text-slate-800">{item.title}</p>
                    <p className="text-[10px] text-slate-400">{item.desc}</p>
                    <Button variant="outline" size="sm" className="text-[10px] gap-1"><ScanLine className="h-3 w-3" />Upload with Lens</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ═══ VISIT TYPES (Settings → Visit Types, per spec Part 2) ═══ */}
        {section === 'visitTypes' && (
          <div data-testid="settings-visit-types">
            <VisitTypeSettings />
          </div>
        )}

        {/* ═══ BOOKING WIDGET (Public patient-facing link) ═══ */}
        {section === 'bookingWidget' && (
          <div data-testid="settings-booking-widget">
            <BookingWidgetSettings />
          </div>
        )}

        {/* ═══ TEAMS ═══ */}
        {section === 'teams' && <TeamsManager />}
      </div>
    </div>
  );
};

export default SchedulerSettings;

/* ═══════════════════════════════════════════════════════════════════════════
   OverbookingSettings — Edit 6 (scheduler_edits_v2)
   ───────────────────────────────────────────────────────────────────────────
   Per-provider-per-location overbooking limits with an Echo AI recommendation
   engine (demo-only — backend weekly recomputation not yet wired). Includes:
     - Stepper control (0–10) per provider
     - Echo AI recommendation banner (Apply / Keep) — demo fixtures only
     - Location-wide cap
     - Auto-adjust toggle (manager opt-in)
     - Approval workflow radio (require/ai-auto/block)
   Real-mode shows a placeholder until the backend scoring model ships.
   ═══════════════════════════════════════════════════════════════════════════ */

const DEMO_OB_LOCATIONS = [
  {
    id: 'main', name: 'Westside Medical — Main Office',
    providers: [
      { id: 'dr-chen',     name: 'Dr. Sarah Chen',     setting: 3, aiRecommendation: 4, showRate: 91, reason: "Dr. Chen's 30-day show rate is 91%. A limit of 4 is statistically safe.", current: '2 overbooked · 0 no-shows from OB', auto: false },
      { id: 'dr-wilson',   name: 'Dr. James Wilson',   setting: 2, aiRecommendation: 2, showRate: 78, reason: "Dr. Wilson's show rate has been 78% this month. Current limit of 2 is appropriate.", current: '1 overbooked · 1 no-show from OB', auto: false },
      { id: 'dr-roberts',  name: 'Dr. Priya Roberts',  setting: 3, aiRecommendation: 2, showRate: 74, reason: 'Show rate dropped to 74% this month. Consider reducing overbooking to avoid scheduling gaps.', current: '3 overbooked · 2 no-shows from OB', auto: false },
    ],
    locationCap: 5,
  },
];

const Stepper = ({ value, onChange, disabled, testId }) => (
  <div className="inline-flex items-center gap-1">
    <button
      onClick={() => !disabled && onChange(Math.max(0, value - 1))}
      disabled={disabled}
      className="inline-flex items-center justify-center"
      style={{ width: 28, height: 28, border: '0.5px solid #ebebeb', borderRadius: 6, background: disabled ? '#fafafa' : '#f5f5f5', cursor: disabled ? 'not-allowed' : 'pointer' }}
      data-testid={`${testId}-dec`}
    >−</button>
    <span style={{ fontSize: 18, color: '#111', fontWeight: 500, minWidth: 32, textAlign: 'center' }} data-testid={`${testId}-value`}>{value}</span>
    <button
      onClick={() => !disabled && onChange(Math.min(10, value + 1))}
      disabled={disabled}
      className="inline-flex items-center justify-center"
      style={{ width: 28, height: 28, border: '0.5px solid #ebebeb', borderRadius: 6, background: disabled ? '#fafafa' : '#f5f5f5', cursor: disabled ? 'not-allowed' : 'pointer' }}
      data-testid={`${testId}-inc`}
    >+</button>
  </div>
);

const OverbookingSettings = ({ allowWrite }) => {
  const { isDemo } = useAppMode();
  const [locations, setLocations] = useState(DEMO_OB_LOCATIONS);
  const [approvalMode, setApprovalMode] = useState('ai_auto_75'); // 'require_manager' | 'ai_auto_75' | 'block'

  if (!isDemo) {
    return (
      <Card className="border-dashed border-slate-200" data-testid="overbooking-empty-real">
        <CardContent className="p-10 text-center">
          <Sparkles className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-600">Overbooking limits are coming soon for real mode</p>
          <p className="text-[11px] text-slate-400 mt-1">Per-provider limits and Echo AI recommendations will populate once the scoring model ships.</p>
        </CardContent>
      </Card>
    );
  }

  const updateProvider = (locId, provId, patch) => {
    setLocations(prev => prev.map(l => l.id !== locId ? l : {
      ...l, providers: l.providers.map(p => p.id === provId ? { ...p, ...patch } : p),
    }));
  };
  const updateLocationCap = (locId, cap) => {
    setLocations(prev => prev.map(l => l.id === locId ? { ...l, locationCap: cap } : l));
  };

  return (
    <div className="space-y-4" data-testid="settings-overbooking">
      <div>
        <p className="text-sm font-semibold text-slate-800">Overbooking limits</p>
        <p className="text-[11px] text-slate-500 mt-1">
          Set how many additional patients can be booked beyond a provider&apos;s full schedule
          for each location. Echo AI can suggest adjustments based on historical show rates.
        </p>
      </div>

      {locations.map(loc => (
        <Card key={loc.id} className="border-slate-200" data-testid={`ob-location-${loc.id}`}>
          <CardContent className="p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-800">▼ {loc.name}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Per-provider limits</p>

            <div className="space-y-3">
              {loc.providers.map(p => {
                const matches = p.setting === p.aiRecommendation;
                const aiHigher = p.aiRecommendation > p.setting;
                return (
                  <div key={p.id} className="p-3 rounded-lg border border-slate-100 space-y-2" data-testid={`ob-provider-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-slate-800">{p.name}</p>
                      <span className="text-[10px] text-slate-400">This week: {p.current}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-slate-500 w-44">Max overbooked patients per day:</span>
                      <Stepper
                        value={p.setting}
                        disabled={p.auto || !allowWrite}
                        onChange={(v) => updateProvider(loc.id, p.id, { setting: v })}
                        testId={`ob-stepper-${p.id}`}
                      />
                      {p.auto && <span className="text-[10px] text-violet-600">Auto-managed by Echo AI</span>}
                    </div>

                    <div className={cn('p-2 rounded-md flex items-start gap-2', aiHigher ? 'bg-emerald-50' : matches ? 'bg-slate-50' : 'bg-amber-50')}>
                      <Sparkles className={cn('h-3 w-3 mt-0.5', aiHigher ? 'text-emerald-600' : matches ? 'text-slate-500' : 'text-amber-600')} />
                      <div className="flex-1">
                        {matches ? (
                          <p className="text-[11px] text-slate-600">Setting matches AI recommendation ✓</p>
                        ) : (
                          <>
                            <p className="text-[11px] text-slate-700">
                              AI recommendation: <b>{p.aiRecommendation}</b>
                              <span className="text-slate-400"> (your setting: {p.setting})</span>
                              {!aiHigher && <span className="ml-1 text-amber-600">⚠</span>}
                            </p>
                            <p className="text-[10px] text-slate-500 mt-0.5">{p.reason}</p>
                            <div className="flex gap-1.5 mt-1.5">
                              <Button size="sm" className="h-6 text-[10px] bg-[#6b5ce7] hover:bg-[#5a4bd6] gap-1" disabled={!allowWrite} onClick={() => updateProvider(loc.id, p.id, { setting: p.aiRecommendation })} data-testid={`ob-apply-${p.id}`}>
                                <Sparkles className="h-2.5 w-2.5" /> Apply AI suggestion
                              </Button>
                              <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => toast.info('Kept manual setting')} data-testid={`ob-keep-${p.id}`}>Keep my setting</Button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                      <Toggle on={p.auto} onToggle={() => updateProvider(loc.id, p.id, { auto: !p.auto, setting: !p.auto ? p.aiRecommendation : p.setting })} testId={`ob-auto-${p.id}`} />
                      <span className="text-[10px] text-slate-600">Let Echo AI auto-adjust this limit weekly</span>
                    </div>
                    {p.auto && (
                      <p className="text-[10px] text-slate-400 pl-10">Echo will apply its recommendation automatically each Monday. <button onClick={() => updateProvider(loc.id, p.id, { auto: false })} className="text-violet-600 underline" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Take manual control</button></p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="pt-2 border-t border-slate-100">
              <p className="text-[11px] text-slate-600">Location-wide cap (optional):</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[10px] text-slate-500">Regardless of per-provider limits, never overbook more than</span>
                <Stepper value={loc.locationCap} disabled={!allowWrite} onChange={(v) => updateLocationCap(loc.id, v)} testId={`ob-loc-cap-${loc.id}`} />
                <span className="text-[10px] text-slate-500">total patients per day at this location.</span>
              </div>
              <p className="text-[9px] text-slate-400 mt-1">Leave at 0 to disable location-wide cap.</p>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Approval workflow */}
      <Card className="border-slate-200" data-testid="ob-approval-workflow">
        <CardContent className="p-4 space-y-2">
          <p className="text-xs font-semibold text-slate-800">When overbooking limit is reached</p>
          {[
            { id: 'require_manager', label: 'Require manager approval before booking', hint: '(recommended)' },
            { id: 'ai_auto_75',      label: 'Auto-approve if Echo AI show probability is ≥ 75%' },
            { id: 'block',           label: 'Block all overbooking — no exceptions' },
          ].map(opt => (
            <label key={opt.id} className="flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer">
              <input
                type="radio"
                name="ob-approval"
                checked={approvalMode === opt.id}
                onChange={() => setApprovalMode(opt.id)}
                disabled={!allowWrite}
                data-testid={`ob-approval-${opt.id}`}
              />
              {opt.label} {opt.hint && <span className="text-slate-400">{opt.hint}</span>}
            </label>
          ))}
          <p className="text-[10px] text-slate-400 pt-1">
            Manager approval requests are sent as in-app alerts and must be resolved within 30 minutes or Echo will auto-decline.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
